import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Zap, Crosshair, Truck, Star, ChevronDown } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_TEL, scrollToQuote } from './shared';

const HERO_IMAGE = 'https://customer-assets-lxgj4vgw.emergentagent.net/job_glass-quote-hub-1/artifacts/5j2x4rom_image.png';

const BENEFITS = [
  { icon: Zap, label: 'Same/Next-\nDay Service' },
  { icon: Crosshair, label: 'ADAS\nCalibration' },
  { icon: Truck, label: 'Mobile\nService' },
];

const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function MobileHero({ rating, totalReviews }) {
  return (
    <section data-testid="mobile-hero" className="relative bg-[#0a1220] text-white overflow-hidden">
      {/* Clean white top that dissolves into the full portrait photo (native aspect ratio, no crop) */}
      <div className="relative bg-white pt-[86px]">
        <img
          src={HERO_IMAGE}
          alt="A+ Auto Glass mobile service van in front of Parliament Hill, Ottawa"
          className="w-full h-auto block"
          fetchPriority="high"
        />
        {/* White wash over the sky, fading away so the photo emerges naturally */}
        <div
          className="absolute inset-x-0 top-0 pointer-events-none"
          style={{
            height: 'min(47%, 366px)',
            backgroundImage:
              'linear-gradient(to bottom, #ffffff 0%, #ffffff 64%, rgba(255,255,255,0.9) 77%, rgba(255,255,255,0.52) 89%, rgba(255,255,255,0) 100%)',
          }}
        />
        {/* Smooth transparent-to-navy blend at the foot of the photo into the CTA area */}
        <div className="absolute inset-x-0 bottom-0 h-[38%] bg-gradient-to-t from-[#0a1220] via-[#0a1220]/70 to-transparent pointer-events-none" />

        {/* Headline content on the white field */}
        <div className="absolute inset-x-0 top-0 px-6 pt-[92px]">
          <motion.div {...fade(0)} className="text-[11px] font-bold tracking-[0.3em] text-[#1F2937]">
            OTTAWA&apos;S AUTO GLASS EXPERTS
          </motion.div>
          <motion.h1 {...fade(0.08)} className="mt-2.5 text-[40px] leading-[0.95] font-black tracking-[-0.02em] text-[#0B1B32]">
            CLEAR VISION.<br /><span className="text-[#0D5BD7]">SAFE DRIVING.</span>
          </motion.h1>
          <motion.p {...fade(0.16)} className="mt-3 text-[16px] text-[#475569] leading-[1.45] max-w-[330px]">
            Windshield Replacement &amp; Auto Glass Services in <span className="text-[#94A3B8]">Ottawa, ON.</span>
          </motion.p>
          <motion.ul {...fade(0.24)} className="mt-6 grid grid-cols-3">
            {BENEFITS.map((b, i) => (
              <li key={b.label} className={`flex flex-col items-center text-center px-1 ${i > 0 ? 'border-l border-[#0B1B32]/15' : ''}`}>
                <b.icon className="w-7 h-7 text-[#0D5BD7]" strokeWidth={2} />
                <span className="mt-2 text-[12.5px] font-bold leading-[1.25] whitespace-pre-line text-[#0B1B32]">{b.label}</span>
              </li>
            ))}
          </motion.ul>
        </div>
      </div>

      {/* CTA panel pulled up to overlap the lower van/road, flowing into navy — no empty gap */}
      <div className="relative z-10 px-6 pb-9 -mt-[26%]">
        <motion.div {...fade(0.38)} className="space-y-3">
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={scrollToQuote}
            data-testid="hero-get-quote-btn"
            className="w-full h-[58px] rounded-full bg-[#0D5BD7] text-white text-[14px] font-bold tracking-[0.22em] flex items-center justify-center gap-3 shadow-[0_12px_34px_rgba(13,91,215,0.55)]"
          >
            GET A QUOTE <ArrowRight className="w-5 h-5" />
          </motion.button>
          <a
            href={PHONE_TEL}
            data-testid="hero-call-btn"
            className="w-full h-[58px] rounded-full border-2 border-white/80 text-white flex items-center justify-center gap-3 bg-white/5 backdrop-blur-md transition-transform active:scale-[0.97]"
          >
            <Phone className="w-[18px] h-[18px]" strokeWidth={2.2} />
            <span className="text-[13px] font-bold tracking-[0.18em]">CALL NOW</span>
            <span className="text-[14px] font-semibold text-white/80">{PHONE_DISPLAY}</span>
          </a>
        </motion.div>

        <motion.div {...fade(0.46)} className="mt-6 flex items-end justify-between">
          <div>
            <div className="flex items-center gap-1.5">
              <div className="flex gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 fill-[#F5B301] text-[#F5B301]" />)}
              </div>
              <span className="text-[14px] font-bold">{Number(rating).toFixed(1)} on Google</span>
            </div>
            {totalReviews && <div className="mt-1 text-[12px] text-white/60">Based on {totalReviews} reviews</div>}
          </div>
          <span className="font-hand text-white/90 text-[19px] leading-[1] -rotate-6 text-right pr-1">Ottawa&apos;s<br />Auto Glass Experts</span>
        </motion.div>

        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
          className="mt-5 flex justify-center text-white/50"
        >
          <ChevronDown className="w-6 h-6" />
        </motion.div>
      </div>
    </section>
  );
}
